import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { money, ROOF_LABELS, SLOT_LABELS } from "../lib/pricing";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  CalendarCheck, DollarSign, ShieldCheck, Zap, Loader2, Truck,
} from "lucide-react";

const STATUS_FLOW = ["scheduled", "en_route", "in_progress", "completed", "paid", "cancelled"];
const STATUS_LABEL = {
  scheduled: "Scheduled", en_route: "En Route", in_progress: "In Progress",
  completed: "Completed", paid: "Paid", cancelled: "Cancelled",
};
const STATUS_STYLE = {
  scheduled: "bg-blue-100 text-blue-700", en_route: "bg-amber-100 text-amber-700",
  in_progress: "bg-purple-100 text-purple-700", completed: "bg-emerald-100 text-emerald-700",
  paid: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-200 text-slate-500",
};

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, b] = await Promise.all([api.get("/admin/metrics"), api.get("/admin/bookings")]);
      setMetrics(m.data);
      setBookings(b.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBookings([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success(`Status → ${STATUS_LABEL[status]}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assignTech = async (id, technician) => {
    try {
      await api.put(`/admin/bookings/${id}/technician`, { technician });
      toast.success("Technician assigned");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cards = [
    { icon: CalendarCheck, label: "Today's Jobs", value: metrics?.todays_jobs ?? "—", color: "text-emerald-700 bg-emerald-50" },
    { icon: DollarSign, label: "Revenue (MTD)", value: metrics ? money(metrics.revenue_mtd) : "—", color: "text-amber-700 bg-amber-50" },
    { icon: ShieldCheck, label: "Veteran Discounts", value: metrics?.veteran_discounts ?? "—", color: "text-slate-700 bg-slate-100" },
    { icon: Zap, label: "Avg Output Gain", value: metrics ? `+${metrics.avg_output_gain}%` : "—", color: "text-purple-700 bg-purple-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center"><Truck className="w-5 h-5 text-amber-400" /></div>
          <div>
            <h1 className="font-heading text-3xl font-black text-slate-900">Dispatch Center</h1>
            <p className="text-slate-500 text-sm">Manage jobs, crews, and the schedule.</p>
          </div>
        </div>

        {/* metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {cards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`metric-${c.label}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="w-5 h-5" /></div>
              <div className="text-2xl font-black font-mono text-slate-900 mt-3">{c.value}</div>
              <div className="text-sm text-slate-500">{c.label}</div>
            </div>
          ))}
        </div>

        {/* jobs table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mt-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-heading font-bold text-slate-900">Job schedule</h2></div>
          {bookings === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-emerald-700" /></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16 text-slate-400" data-testid="admin-empty">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead><TableHead>Date / Slot</TableHead><TableHead>Job</TableHead>
                    <TableHead>Total</TableHead><TableHead>Pay</TableHead><TableHead>Technician</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`admin-row-${b.id}`}>
                      <TableCell>
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">{b.user_name}{b.is_veteran && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}</div>
                        <div className="text-xs text-slate-400">{b.city}</div>
                      </TableCell>
                      <TableCell className="text-sm">{b.service_date}<div className="text-xs text-slate-400">{SLOT_LABELS[b.time_slot]}</div></TableCell>
                      <TableCell className="text-sm">{b.panels}p · {ROOF_LABELS[b.roof_type]}</TableCell>
                      <TableCell className="font-mono font-semibold">{money(b.total)}</TableCell>
                      <TableCell><Badge variant="outline" className={b.payment_status === "paid" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{b.payment_status === "paid" ? "Paid" : "Unpaid"}</Badge></TableCell>
                      <TableCell>
                        <Input defaultValue={b.technician || ""} placeholder="Assign…" data-testid={`tech-input-${b.id}`}
                          onBlur={(e) => { if (e.target.value !== (b.technician || "")) assignTech(b.id, e.target.value); }}
                          className="h-9 w-28 text-sm" />
                      </TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className="h-9 w-36" data-testid={`status-select-${b.id}`}>
                            <SelectValue><Badge className={`${STATUS_STYLE[b.status]} border-0`}>{STATUS_LABEL[b.status]}</Badge></SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
